'use client';

import {
  ComponentPropsWithRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactMapGL, {
  MapRef,
  ViewState,
  ViewStateChangeEvent,
} from 'react-map-gl/maplibre';
import { twMerge } from 'tailwind-merge';
import useSupercluster from 'use-supercluster';

import { useZmyleCookieStore } from '@/hooks/useZmyleCookieStore';
import { ClientConfig } from '@/services/clientConfig';

import { ClusterMarker } from './ClusterMarker';
import { EnableMaptilerCookie } from './EnableMaptilerCookie';
import { calculateMapCenterCoordinates } from './lib';
import { StoreMarker } from './StoreMarker';

import 'maplibre-gl/dist/maplibre-gl.css';
import './map.css';

type PartialViewState = Pick<ViewState, 'latitude' | 'longitude' | 'zoom'>;

const initialViewState: PartialViewState = {
  longitude: 10.4515,
  latitude: 51.1657,
  zoom: 6,
};

export type MapProps = Omit<ComponentPropsWithRef<'div'>, 'content'> & {
  stores: AdaptedStore[] | AdaptedStore<true>[];
  content: OutletsMapContent;
  initialZoom?: number;
  openStorePreviewOnClick?: boolean;
};

export function Map({
  stores: _stores,
  className,
  children,
  content,
  initialZoom,
  openStorePreviewOnClick = true,
  ...props
}: MapProps) {
  const [viewState, setViewState] =
    useState<PartialViewState>(initialViewState);

  const mapRef = useRef<MapRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const zmyleCookie = useZmyleCookieStore(state => state.zmyleCookie);

  const isMaptilerEnabled = zmyleCookie.consents.media.includes('maptiler');

  const stores = useMemo<AdaptedStore[] | AdaptedStore<true>[]>(() => {
    return _stores.filter(store => {
      return store.address.geometry?.lon && store.address.geometry?.lat;
    }) as AdaptedStore[] | AdaptedStore<true>[];
  }, [_stores]);

  useEffect(() => {
    if (!mapRef.current || !containerRef.current) return;

    const { width, height } = containerRef.current.getBoundingClientRect();

    const coordinates = calculateMapCenterCoordinates({
      stores,
      mapHeight: height,
      mapWidth: width,
    });

    if (!coordinates) return;

    mapRef.current.flyTo({
      animate: true,
      speed: 2,
      center: [coordinates.longitude, coordinates.latitude],
      zoom: initialZoom || coordinates.zoom,
    });
  }, [stores]);

  const instructions = useMemo(() => {
    const { mapZoomInstructions, mapInstructionsMobile } = content;

    const key = /<key>|<<key>>/;

    return {
      windows: mapZoomInstructions.replace(key, 'Ctrl'),
      mac: mapZoomInstructions.replace(key, '⌘'),
      mobile: mapInstructionsMobile,
    };
  }, []);

  const { clusters, supercluster } = useSupercluster({
    points: stores.map(store => ({
      type: 'Feature',
      properties: { cluster: false, store },
      geometry: {
        type: 'Point',
        coordinates: [
          store.address.geometry?.lon || 0,
          store.address.geometry?.lat || 0,
        ],
      },
    })),
    // @ts-expect-error: use-supercluster typing are very bad
    bounds: mapRef.current?.getMap().getBounds().toArray().flat() || undefined,
    zoom: viewState.zoom,
    options: { radius: 75, maxZoom: 16 },
  });

  function onMove(event: ViewStateChangeEvent) {
    setViewState({ ...event.viewState });
  }

  function onClusterClick({
    clusterId,
    lat,
    long,
  }: {
    clusterId: string | number | undefined;
    lat: number;
    long: number;
  }) {
    if (clusterId === undefined) return;

    const expansionZoom = Math.min(
      supercluster?.getClusterExpansionZoom(Number(clusterId)) || 0,
      20,
    );

    mapRef.current?.flyTo({
      animate: true,
      speed: 2,
      center: [long, lat],
      zoom: expansionZoom,
    });
  }

  function onLoad() {
    if (!containerRef.current || !mapRef.current) {
      setViewState({ ...initialViewState });

      return;
    }

    const { width, height } = containerRef.current.getBoundingClientRect();

    const coordinates = calculateMapCenterCoordinates({
      stores,
      mapHeight: height,
      mapWidth: width,
    });

    if (!coordinates) return;

    mapRef.current.flyTo({
      animate: true,
      speed: 2,
      center: [coordinates.longitude, coordinates.latitude],
      zoom: initialZoom || coordinates.zoom,
    });
  }

  const clientConfig = ClientConfig.getInstance().getConfig();

  if (!isMaptilerEnabled) {
    return (
      <div className={twMerge('relative flex size-full', className)}>
        <EnableMaptilerCookie content={content} />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={twMerge('relative flex size-full', className)}
      {...props}
    >
      <ReactMapGL
        {...viewState}
        ref={mapRef}
        onMove={onMove}
        initialViewState={initialViewState}
        mapStyle={clientConfig.maptilerUrl}
        onLoad={onLoad}
        cooperativeGestures
        locale={{
          'CooperativeGesturesHandler.WindowsHelpText': instructions.windows,
          'CooperativeGesturesHandler.MacHelpText': instructions.mac,
          'CooperativeGesturesHandler.MobileHelpText': instructions.mobile,
        }}
      >
        {clusters.map(cluster => {
          const [longitude, latitude] = cluster.geometry.coordinates;
          const {
            cluster: isCluster,
            store,
            // @ts-expect-error: use-supercluster typing is very bad
            point_count: pointCount,
          } = cluster.properties;

          if (isCluster)
            return (
              <ClusterMarker
                key={`cluster-${cluster.id}`}
                latitude={latitude}
                longitude={longitude}
                onClick={onClusterClick}
                clusterId={cluster.id}
                pointCount={pointCount}
              />
            );

          return (
            <StoreMarker
              key={store.id}
              store={store}
              openPreviewOnClick={openStorePreviewOnClick}
            />
          );
        })}

        {children}
      </ReactMapGL>
    </div>
  );
}
